import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FormBlock, FormCard, ValidationList } from '@qbe/ads-blocks-forms';
import { Box, Button, FormGroupLabel, Spacer, Typography, useTheme } from '@qbe/ads-core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import EmailQuoteModal from '~/components/EmailQuoteModal';
import LevelOfExcess from '~/components/LevelOfExcess';
import PartnerBanner from '~/components/PartnerBanner';
import PaymentSchedule from '~/components/PaymentSchedule';
import Loading from '~/components-ds/Loading';
import Modal from '~/components-ds/Modal';
import TotalPrice from '~/components-ds/TotalPrice';
import { EXTRA_INFO_CUSTOMER_HISTORY_ROUTE } from '~/constants/routes';
import {
  useAppContent,
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
  useBusinessDetails,
  useQuote,
} from '~/hooks';
import { useErrorHandler } from '~/hooks/useErrorHandler';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { selectIsReferral } from '~/selectors/selectAppDetails';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { useLazyGetHistoryQuestionsQuery } from '~/services/history';
import { updateHistoryQuestions } from '~/slices/extraInfo';
import { updateQuoteDateUpdated } from '~/slices/quote';
import {
  qalFormCompleteAction,
  qalFormStartAction,
  qalTrackEngagementAction,
  qalTrackEventAction,
} from '~/thunks/analytics';
import { navigateAction } from '~/thunks/navigateAction';
import { quotePremiumsAction } from '~/thunks/quotePremiumsAction';
import { PaymentSchedule as PaymentScheduleOptions } from '~/types/payments';
import { getMonthlyPremium, utcDateFormatter } from '~/utils';
import { getActivePagePath } from '~/utils/analytics/helpers';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';
import { currencyFormatter } from '~/utils/currencyFormatter';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const Quote: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { coverStart } = useBusinessDetails();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);
  const isReferral = useAppSelector(selectIsReferral);

  const { getTextContent } = useAppContent({
    basePath: 'dsme.quote',
  });
  const { getTextContent: getTextContentModal } = useAppContent({
    basePath: 'dsme.quoteModal',
  });

  const { getTextContent: getSharedText } = useAppContent({
    basePath: 'shared',
  });

  const [getHistoryQuestions, { data, error, isLoading }] = useLazyGetHistoryQuestionsQuery();
  useRTKQErrorHandler(error);
  const { premiums, paymentSchedule, quoteDateUpdated } = useQuote();
  const isMonthlySchedule = paymentSchedule === PaymentScheduleOptions.Monthly;
  const premium = isMonthlySchedule
    ? getMonthlyPremium(premiums?.policy?.payment?.instalments?.[1])
    : premiums?.policy?.policyPremium?.transactionPremium?.totalPremium;
  const [isLoadingPremiums, setIsLoadingPremiums] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isPaymentFreqModalOpen, setIsPaymentFreqModalOpen] = useState(false);

  const errorHandler = useErrorHandler();

  const handleFormStart = () => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  };

  const handleFormComplete = () => {
    if (isFormStartStarted()) dispatch(qalFormCompleteAction());
  };

  useEffect(() => {
    const update = async () => {
      setIsLoadingPremiums(true);
      await dispatch(quotePremiumsAction());
      setIsLoadingPremiums(false);
      setIsPaymentFreqModalOpen(true);
    };
    if (isMonthlySchedule && !premium) {
      update().catch(errorHandler);
    }
  }, [
    dispatch,
    errorHandler,
    isMonthlySchedule,
    premium,
    quoteDateUpdated,
    setIsPaymentFreqModalOpen,
  ]);

  useEffect(() => {
    if (data?.questions) {
      dispatch(updateHistoryQuestions(data?.questions));
      dispatch(
        navigateAction({
          to: EXTRA_INFO_CUSTOMER_HISTORY_ROUTE,
          navigate,
        })
      );
    }
  }, [data, dispatch, navigate]);

  useEffect(() => {
    // If premiums are still loading, do nothing.
    // We wait until the premium API call is completed before proceeding.
    if (isLoadingPremiums) return;

    // Dispatch the "optimize.activate" event to the QAL (QBE Analytics Library).
    // This notifies Webtrends or other analytics tools that the premium information
    // is now available, enabling accurate visibility tracking for dynamic content.
    dispatch(
      qalTrackEventAction({
        event: 'optimize.activate',
        data: {
          virtualPagePath: getActivePagePath(),
        },
      })
    );
  }, [isLoadingPremiums, dispatch]);

  const handleNext = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    handleFormStart();
    handleFormComplete();
    getHistoryQuestions({
      effectiveDate: utcDateFormatter(coverStart!),
    });
  };

  const onClose = () => {
    dispatch(updateQuoteDateUpdated(false));
  };

  const onCloseHandler = () => {
    setIsModalOpen(false);
    dispatch(
      qalTrackEngagementAction({
        eventAction: 'modal: close',
        eventLabel: 'email quote',
        data: {},
      })
    );
  };

  /**
   * @TODO
   * If no premium returned then show proper message
   */

  return (
    <>
      <FormCard
        description={getTextContent('description')}
        title={getTextContent('title')}
        variant="pageTitle"
        customElement={isReferral ? <PartnerBanner /> : null}
      />

      <FormCard variant="content" disablePadding>
        <Box
          width="100%"
          sx={{
            background: theme.palette.grey[100],
          }}
          padding={3}
          textAlign="center"
        >
          {isLoadingPremiums ? (
            <Loading text={getTextContent('content.loadingPremiumText.title')} />
          ) : (
            <TotalPrice price={currencyFormatter(premium)} />
          )}

          <Box display="flex" justifyContent="center">
            <PaymentSchedule isLoading={isLoadingPremiums} />
          </Box>
          <Spacer size="xl" />

          {isLoading ? (
            <Loading />
          ) : (
            <Button
              data-track="review and buy now"
              onClick={handleNext}
              disabled={isLoading || isLoadingPremiums}
              label={getTextContent('buttons.reviewBuy')}
            />
          )}
          <Spacer size="md" />

          <ValidationList
            list={[
              {
                id: '1',
                item: 'Australian call centre',
              },
              {
                id: '2',
                item: "130+ years' experience",
              },
              {
                id: '3',
                item: '24/7 claims service',
              },
              {
                id: '4',
                item: 'Trusted Australian brand',
              },
            ]}
          />
        </Box>

        <Box padding={4} textAlign="center">
          <FormGroupLabel
            title={getTextContent('fields.levelOfExcess.title')}
            titleSuffix={
              <ContextualHelpWithButton tracking="help: Level of excess">
                <Typography fontWeight="fontWeightBold" gutterBottom>
                  {getTextContent('contextualhelpers.levelOfExcess.title')}
                </Typography>
                <Typography>
                  {getTextContent('contextualhelpers.levelOfExcess.description')}
                </Typography>
              </ContextualHelpWithButton>
            }
            subtitle={getTextContent('fields.levelOfExcess.description')}
            disableBlockPadding
          />

          <LevelOfExcess setIsLoadingPremiums={setIsLoadingPremiums} />
          <Spacer size="md" />

          <Typography>{getTextContent('content.disclaimer.description')}</Typography>

          {!isPropertyOwner && (
            <FormBlock preset="button">
              <Button
                variant="secondary-on-light"
                data-track="email indicative quote to myself"
                onClick={() => {
                  handleFormStart();
                  setIsModalOpen(true);
                }}
                disabled={isLoading || isLoadingPremiums}
                label={getTextContent('buttons.emailQuote')}
              />
            </FormBlock>
          )}
          {isPropertyOwner && (
            <>
              <Typography variant="body2">
                {getTextContent('content.newQuoteReminder.description')}
              </Typography>
              <Spacer size="xl" />
            </>
          )}
        </Box>
      </FormCard>

      {isModalOpen && <EmailQuoteModal isOpen onClose={onCloseHandler} />}

      <Modal
        id="header=quote-updated-modal"
        analyticsModalName="header quote date updated modal"
        open={Boolean(quoteDateUpdated)}
        title={getTextContentModal('content.quoteUpdated.title')}
        iconProps={{
          muiIcon: InfoOutlined,
          // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          color: theme.color.icon.info,
        }}
        onClose={onClose}
        primaryBtnProps={{
          onClick: onClose,
          label: getSharedText('gotItButtonLabel'),
        }}
      >
        <Typography>{getTextContentModal('content.quoteUpdated.description')}</Typography>
      </Modal>

      {isPaymentFreqModalOpen && (
        <Modal
          id="payment-frequency-modal"
          analyticsModalName="payment frequency changed modal"
          open
          title={getTextContentModal('content.paymentFrequency.title')}
          iconProps={{
            muiIcon: InfoOutlined,
            // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            color: theme.color.icon.info,
          }}
          onClose={() => setIsPaymentFreqModalOpen(false)}
          primaryBtnProps={{
            onClick: () => setIsPaymentFreqModalOpen(false),
            label: getSharedText('gotItButtonLabel'),
          }}
        >
          <Typography>{getTextContentModal('content.paymentFrequency.description')}</Typography>
        </Modal>
      )}
    </>
  );
};

export default Quote;
